import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getPuzzleConcept } from '../../data/puzzleConcepts';
import './GameHeader.css';

/**
 * Floating gaming icons for puzzle controls (no header band)
 * @param {string} title - Game title (optional, unused in floating design)
 * @param {Function} onBack - Callback when back to room is clicked
 * @param {string} backLabel - Tooltip text for the Back icon (defaults to "Back to Room";
 *   callers whose Back target isn't the room - e.g. lobby -> hallway - override this)
 * @param {Function} onShowHint - Callback when hint button is clicked
 * @param {Function} onShowExplanation - Callback when explain button is clicked (opens the
 *   concept guide - always available, not gated behind solving the puzzle)
 * @param {Function} onShowHowToPlay - Callback for the ❓ How to Play icon (opens the room's
 *   how-to-play guide). Omitted entirely (no icon rendered) by callers with no guide to show -
 *   same optional-icon pattern as onBack/onExitGame below. Shown regardless of
 *   showPuzzleControls since it's relevant even before a puzzle is open.
 * @param {Function} onReplayDialogue - Callback for the 💬 Replay Intro icon (replays this
 *   screen's entry dialogue on demand, since it only auto-plays once per browser session -
 *   see utils/dialogueSeen.js). Omitted entirely by callers with no entry dialogue to replay,
 *   same optional-icon pattern as onShowHowToPlay.
 * @param {Function} onLearn - Callback when learn button is clicked (navigates to Coach)
 * @param {number} availableHints - Number of hints available
 * @param {number} usedHints - Number of hints already used
 * @param {string} puzzleType - Type of puzzle; looked up in puzzleConcepts.js for the Explain/
 *   Learn icons' concept name and starter query
 * @param {string} selectedDifficulty - This puzzle's current difficulty level ('easy' | 'medium' | 'hard')
 * @param {Function} onDifficultyChange - Optional callback(newDifficulty) for the +/- difficulty
 *   control. Omitted entirely (no icon rendered) by callers that don't support changing
 *   difficulty mid-puzzle.
 * @param {Function} onExitGame - Callback for the Exit Game icon, always rendered last in the
 *   column. Omitted entirely (no icon rendered) by callers that don't offer a game-level exit
 *   here (e.g. a screen that's already showing its own).
 * @param {boolean} showConceptIcons - Default true. false hides Explain/Learn while keeping Hint
 *   (for a screen with no curriculum concept behind it, e.g. game-builder missions).
 * @param {boolean} showPuzzleControls - Gates Hint/Explain/Learn/Difficulty, which only make
 *   sense once an actual puzzle is open. Screens outside a puzzle (lobby, room exploration)
 *   leave this false and just get Back/Exit.
 *
 * The ⛶ Fullscreen toggle takes no prop - it's self-contained, toggling the real browser
 * Fullscreen API on document.documentElement wherever this header appears, and renders
 * itself out entirely where unsupported (e.g. iOS Safari, which has no Fullscreen API for
 * anything but <video>).
 */
// Only used to translate this room's difficulty into a plausible starting grade when handing
// off to the Coach (handleLearn below) - Coach is a separate domain still keyed on numeric
// grade, untouched by this room's difficulty system.
const DIFFICULTY_TO_REPRESENTATIVE_GRADE = { easy: 4, medium: 7, hard: 10 };

// Ordered so -/+ can just step an index - also drives the badge shown on the icon itself.
const DIFFICULTY_STEPS = [
  { id: 'easy', label: 'Easy', emoji: '🟢' },
  { id: 'medium', label: 'Medium', emoji: '🟡' },
  { id: 'hard', label: 'Hard', emoji: '🔴' }
];

// Real browser fullscreen (hides the address bar/tabs, unlike the app's existing CSS-only
// "fullscreen" containers which just fill the viewport). Vendor-prefixed fallbacks cover
// older desktop Safari; there is no fallback for iOS Safari, which doesn't implement this API
// for anything but <video> - fullscreenSupported below is false there, so the icon just
// doesn't render rather than showing a button that would silently do nothing.
const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement || null;

const requestFullscreen = (el) => {
  const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
  if (fn) fn.call(el);
};

const exitFullscreen = () => {
  const fn = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
  if (fn) fn.call(document);
};

const isFullscreenSupported = () => {
  const el = document.documentElement;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen);
};

function GameHeader({
  title,
  onBack,
  backLabel = 'Back to Room',
  onShowHint,
  onShowExplanation,
  onShowHowToPlay,
  onReplayDialogue,
  onLearn,
  availableHints = 3,
  usedHints = 0,
  puzzleType = 'algebra',
  // Already-resolved { concept, query, subject, subTopic } - lets a caller that knows the
  // actual loaded puzzle INSTANCE (not just its type) override the generic per-type lookup
  // below, e.g. PuzzleChrome.jsx passing getInstancePuzzleConcept's result for history's 3
  // puzzle types. Every other caller simply doesn't pass this, so getPuzzleConcept(puzzleType)
  // keeps behaving exactly as before for every other game.
  conceptOverride = null,
  selectedDifficulty = null,
  onDifficultyChange = null,
  onExitGame = null,
  showPuzzleControls = false,
  // Optional "Notes" icon, rendered FIRST in the icon column - lets a puzzle's own
  // ScientistNote live inside this same collapsible menu instead of floating as a second,
  // separate icon next to it (see ScientistNote.jsx's own `collapsed`/`onToggleCollapsed`
  // controlled mode, which this is meant to drive). Omitted entirely (no icon rendered) by
  // every caller that hasn't opted into that - same optional-icon pattern as onExitGame etc.
  notesOpen = false,
  onToggleNotes = null,
  // Explain/Learn are about a curriculum concept (puzzleConcepts.js). A screen with hints but no
  // concept behind it (game-builder missions) passes false to keep just the hint icon.
  showConceptIcons = true
}) {
  const navigate = useNavigate();
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hintsRemaining = Math.max(0, availableHints - usedHints);

  const conceptInfo = conceptOverride || getPuzzleConcept(puzzleType);

  // Self-contained (no props needed) since it's the same browser-level toggle wherever this
  // header appears - lobby, room exploration, or an open puzzle. Support is checked once per
  // mount rather than stored in state since it can't change during the page's lifetime.
  const [fullscreenSupported] = useState(isFullscreenSupported);
  const [isFullscreen, setIsFullscreen] = useState(() => !!getFullscreenElement());

  useEffect(() => {
    if (!fullscreenSupported) return undefined;
    const handleChange = () => setIsFullscreen(!!getFullscreenElement());
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    document.addEventListener('MSFullscreenChange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
      document.removeEventListener('MSFullscreenChange', handleChange);
    };
  }, [fullscreenSupported]);

  const handleToggleFullscreen = () => {
    if (getFullscreenElement()) {
      exitFullscreen();
    } else {
      requestFullscreen(document.documentElement);
    }
  };

  const handleLearn = () => {
    if (onLearn) {
      onLearn();
    } else {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      if (!token) {
        // Navigate to login page
        alert('Please log in to access the AI coach!');
        navigate('/login');
        return;
      }

      // Navigate to the dashboard's Coach section, pre-seeded with this puzzle's own
      // concept as the session topic and autoStart so Coach skips its subject/sub-topic
      // form and jumps straight into an assessment for that specific concept, instead of
      // a broad subject. Coach is a separate domain still keyed on numeric grade (not part
      // of this room's difficulty system), so this maps our 3-level difficulty to a
      // representative grade purely for that handoff.
      //
      // subject/subTopic (Coach's own fixed taxonomy, see coachSubjects.js) ride along too -
      // NOT as the session topic (that stays the specific concept above), but so
      // CoachSection.js can seed its subject/subTopic state the same way the manual
      // dropdown flow would. Without them, session.topic is a bare concept string with no
      // "Subject: SubTopic" shape to parse a subject back out of, which left subject-driven
      // Coach features (Set Treasure Hunt, Play Escape Room) with an empty subject for any
      // session started from here.
      navigate('/dashboard/coach', {
        state: {
          autoStart: true,
          grade: DIFFICULTY_TO_REPRESENTATIVE_GRADE[selectedDifficulty] || '',
          concept: conceptInfo.concept,
          query: conceptInfo.query,
          subject: conceptInfo.subject,
          subTopic: conceptInfo.subTopic
        }
      });
    }
  };

  const difficultyIndex = DIFFICULTY_STEPS.findIndex((d) => d.id === selectedDifficulty);
  const currentDifficulty = difficultyIndex >= 0 ? DIFFICULTY_STEPS[difficultyIndex] : null;

  const stepDifficulty = (delta) => {
    if (!onDifficultyChange || difficultyIndex < 0) return;
    const nextIndex = difficultyIndex + delta;
    if (nextIndex < 0 || nextIndex >= DIFFICULTY_STEPS.length) return;
    onDifficultyChange(DIFFICULTY_STEPS[nextIndex].id);
  };

  return (
    <>
      {/* Menu Toggle - Top Right, every viewport. Consolidates Back/Hint/Explain/Learn
          behind a single tap target, collapsed by default, so the icon column never
          sits on top of a puzzle's own scene/controls. */}
      <motion.div
        className="floating-game-icon menu-toggle-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        onClick={() => setIsMenuOpen((prev) => !prev)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">{isMenuOpen ? '✕' : '☰'}</span>
      </motion.div>

      <div className={`game-icons-menu ${isMenuOpen ? 'open' : ''}`}>

      {/* Notes Icon - always first in the column when a caller has a ScientistNote to drive
          (see ScientistNote.jsx's controlled `collapsed`/`onToggleCollapsed` mode). This
          replaces that note's own separate floating icon button - one merged entry point
          instead of two nearby circles. Light parchment background (not the dark glass every
          other icon here uses) so it still reads as "the note," just relocated. */}
      {onToggleNotes && (
      <motion.div
        className={`floating-game-icon notes-icon ${notesOpen ? 'is-active' : ''}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.05 }}
        onMouseEnter={() => setHoveredIcon('notes')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={onToggleNotes}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">📝</span>
        <AnimatePresence>
          {hoveredIcon === 'notes' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {notesOpen ? 'Hide Notes' : 'Show Notes'}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Back Icon - grouped with the rest of the icons on the right instead
          of sitting alone opposite them on the top-left, where it used to
          collide with the room nav sidebar's own collapsed burger toggle.
          Only rendered when a caller actually has somewhere to go back to. */}
      {onBack && (
      <motion.div
        className="floating-game-icon back-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        onMouseEnter={() => setHoveredIcon('back')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={onBack}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">←</span>
        <AnimatePresence>
          {hoveredIcon === 'back' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {backLabel}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* How to Play Icon - grouped right after Back since it's about orienting the player
          in the room, not a puzzle-specific control (unlike Hint/Explain/Learn below, this
          one isn't gated behind showPuzzleControls). Only rendered for rooms that actually
          have a guide to show (see EscapeRoomGame.jsx's HOW_TO_PLAY_GUIDES lookup). */}
      {onShowHowToPlay && (
      <motion.div
        className="floating-game-icon how-to-play-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.15 }}
        onMouseEnter={() => setHoveredIcon('how-to-play')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={onShowHowToPlay}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">❓</span>
        <AnimatePresence>
          {hoveredIcon === 'how-to-play' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              How to Play
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Replay Intro Icon - grouped right after How to Play, same reasoning: orienting the
          player, not a puzzle-specific control. Only rendered for screens that actually have
          an entry dialogue (the auto-played sequence only plays once per browser session -
          see utils/dialogueSeen.js - so this is the only way back to it afterwards). */}
      {onReplayDialogue && (
      <motion.div
        className="floating-game-icon replay-dialogue-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.17 }}
        onMouseEnter={() => setHoveredIcon('replay-dialogue')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={onReplayDialogue}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">💬</span>
        <AnimatePresence>
          {hoveredIcon === 'replay-dialogue' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              Replay Intro
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Fullscreen Toggle - hides the browser's own address bar/tabs (unlike the app's
          existing CSS-only "fullscreen" containers, which only fill the viewport). Only
          rendered where the browser actually supports it - notably absent on iOS Safari,
          which has no Fullscreen API for anything but <video>, so there's no button that
          would silently do nothing there. */}
      {fullscreenSupported && (
      <motion.div
        className={`floating-game-icon fullscreen-icon ${isFullscreen ? 'is-fullscreen' : ''}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.18 }}
        onMouseEnter={() => setHoveredIcon('fullscreen')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={handleToggleFullscreen}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">⛶</span>
        <AnimatePresence>
          {hoveredIcon === 'fullscreen' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Difficulty Control - shown regardless of showPuzzleControls (same reasoning as
          onShowHowToPlay above: relevant even before a puzzle is open, e.g. the Chronicle
          Archive's library view changing which grade-level shelf is shown) whenever the caller
          actually supports changing difficulty (passes both a recognized selectedDifficulty and
          onDifficultyChange) - refreshing the puzzle/content on change is the caller's job, not
          this component's - see each room's own onDifficultyChange handler. */}
      {onDifficultyChange && currentDifficulty && (
        <motion.div
          className="floating-game-icon difficulty-control-icon"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
          onMouseEnter={() => setHoveredIcon('difficulty')}
          onMouseLeave={() => setHoveredIcon(null)}
        >
          <div className="difficulty-control-row">
            <button
              type="button"
              className="difficulty-step-btn"
              disabled={difficultyIndex >= DIFFICULTY_STEPS.length - 1}
              onClick={() => stepDifficulty(1)}
              aria-label="Increase difficulty"
            >
              +
            </button>
            <span className="icon-symbol difficulty-current-badge">{currentDifficulty.emoji}</span>
            <button
              type="button"
              className="difficulty-step-btn"
              disabled={difficultyIndex <= 0}
              onClick={() => stepDifficulty(-1)}
              aria-label="Decrease difficulty"
            >
              −
            </button>
          </div>
          <AnimatePresence>
            {hoveredIcon === 'difficulty' && (
              <motion.div
                className="icon-label"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                Difficulty: {currentDifficulty.label}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Hint Icon - Top Right */}
      {showPuzzleControls && (
      <motion.div
        className={`floating-game-icon hint-icon ${hintsRemaining === 0 ? 'disabled' : ''}`}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        onMouseEnter={() => setHoveredIcon('hint')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={hintsRemaining > 0 ? onShowHint : null}
        whileHover={hintsRemaining > 0 ? { scale: 1.1, rotate: 10 } : {}}
        whileTap={hintsRemaining > 0 ? { scale: 0.9 } : {}}
      >
        <span className="icon-symbol">💡</span>
        {hintsRemaining > 0 && <span className="icon-badge">{hintsRemaining}</span>}
        <AnimatePresence>
          {hoveredIcon === 'hint' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              {hintsRemaining > 0 ? `${hintsRemaining} Hints Available` : 'No Hints Left'}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Explain Icon - Right Side Below Hint. Opens a one-page concept guide (see
          PuzzleChrome.jsx) - always available, unlike the old "View Solution" behavior
          this replaced, which stayed locked until the puzzle was solved. */}
      {showPuzzleControls && showConceptIcons && (
      <motion.div
        className="floating-game-icon explain-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        onMouseEnter={() => setHoveredIcon('explain')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={onShowExplanation}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">📖</span>
        <AnimatePresence>
          {hoveredIcon === 'explain' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              Learn the Concept
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Learn Icon - Right Side Below Explain */}
      {showPuzzleControls && showConceptIcons && (
      <motion.div
        className="floating-game-icon learn-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4 }}
        onMouseEnter={() => setHoveredIcon('learn')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={handleLearn}
        whileHover={{ scale: 1.1, rotate: -5 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">🎓</span>
        <AnimatePresence>
          {hoveredIcon === 'learn' && (
            <motion.div
              className="icon-label learn-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="learn-title">Learn: {conceptInfo.concept}</div>
              <div className="learn-subtitle">Chat with AI tutor</div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      {/* Exit Game Icon - always last in the column, wherever this menu is rendered
          (lobby, room exploration, or an active puzzle). */}
      {onExitGame && (
      <motion.div
        className="floating-game-icon exit-icon"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
        onMouseEnter={() => setHoveredIcon('exit')}
        onMouseLeave={() => setHoveredIcon(null)}
        onClick={onExitGame}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <span className="icon-symbol">🚪</span>
        <AnimatePresence>
          {hoveredIcon === 'exit' && (
            <motion.div
              className="icon-label"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
            >
              Exit
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      )}

      </div>
    </>
  );
}

export default GameHeader;
